import React from 'react';
import { useNavigate } from 'react-router-dom';
import { PageLayout } from '@/shared/layout';
import { Button, Filter, Search, KPICard, Card, Badge, Table, Avatar } from '@/components/ui';
import { useConstructionPaymentClaims } from './hooks/useConstructionPayment';
import { useAuth } from '@/core/auth/AuthContext';
import { AuthButton } from '@/core/permissions/AuthButton';
import type { PaymentClaimStatus, PaymentClaimType } from './types/construction-payment.types';

const statusColors: Record<PaymentClaimStatus, string> = {
  draft: 'bg-slate-100 text-slate-500',
  submitted: 'bg-amber-100 text-amber-600',
  under_review: 'bg-blue-100 text-blue-600',
  approved: 'bg-emerald-500 text-white',
  rejected: 'bg-red-500 text-white',
  partially_paid: 'bg-indigo-100 text-indigo-600',
  paid: 'bg-indigo-500 text-white',
  cancelled: 'bg-slate-900 text-white',
};

const typeLabels: Record<PaymentClaimType, string> = {
  advance: 'Tạm ứng',
  progress: 'Thanh toán đợt',
  milestone: 'Cột mốc',
  variation: 'Phát sinh',
  final: 'Quyết toán',
  retention_release: 'Hoàn giữ lại',
};

export default function PaymentClaimListPage() {
  const { profile } = useAuth();
  const companyId = profile?.company_id || '';
  const navigate = useNavigate();

  const {
    claims,
    summary,
    isLoading,
  } = useConstructionPaymentClaims(companyId);

  return (
    <PageLayout
      title="Hồ sơ Thanh toán Thi công"
      description="Quản lý đề nghị thanh toán (Payment Claims), đối soát khối lượng nghiệm thu và theo dõi dòng tiền dự án."
      breadcrumbs={[{ label: 'Thi công' }, { label: 'Thanh toán' }]}
      isLoading={isLoading}
      headerActions={
        <div className="flex gap-2">
          <Button variant="outline" icon="ri-file-chart-line">Báo cáo công nợ</Button>
          <AuthButton
            module="construction"
            action="create_payment_claim"
            variant="primary"
            icon="ri-add-line"
            onClick={() => navigate('./create')}
          >
            Tạo đề nghị mới
          </AuthButton>
        </div>
      }
    >
      <div className="space-y-6">
        {/* KPI Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <KPICard
            title="Đã đề nghị (Net)"
            value={summary?.totalClaimed.toLocaleString() || 0}
            icon="ri-bank-card-line"
            variant="primary"
            suffix=" VNĐ"
          />
          <KPICard
            title="Đang giữ lại"
            value={summary?.retentionHeld.toLocaleString() || 0}
            icon="ri-safe-2-line"
            variant="warning"
            suffix=" VNĐ"
          />
          <KPICard
            title="Tổng thanh toán"
            value={(summary?.totalPaid || 0).toLocaleString()}
            icon="ri-money-dollar-circle-line"
            variant="success"
            suffix=" VNĐ"
          />
          <KPICard
            title="Dư nợ hợp đồng"
            value={(summary?.remainingBalance || 0).toLocaleString()}
            icon="ri-scales-3-line"
            variant="info"
            suffix=" VNĐ"
          />
        </div>

        <PageLayout.Toolbar>
          <div className="flex flex-col md:flex-row gap-4 w-full justify-between items-start md:items-center">
            <div className="flex flex-wrap gap-3 flex-1">
              <Search
                placeholder="Tìm mã hồ sơ, tên đợt thanh toán..."
                className="w-full md:w-80"
              />
              <Filter
                options={[
                  {
                    id: 'type', label: 'Loại thanh toán', type: 'select', choices: [
                      { value: 'all', label: 'Tất cả loại' },
                      { value: 'advance', label: 'Tạm ứng' },
                      { value: 'progress', label: 'Thanh toán đợt' },
                      { value: 'variation', label: 'Phát sinh' },
                      { value: 'final', label: 'Quyết toán' }
                    ]
                  },
                  {
                    id: 'status', label: 'Trạng thái', type: 'select', choices: [
                      { value: 'all', label: 'Tất cả trạng thái' },
                      { value: 'submitted', label: 'Đã gửi' },
                      { value: 'approved', label: 'Đã duyệt' },
                      { value: 'paid', label: 'Đã thanh toán' }
                    ]
                  }
                ]}
                onFilterChange={() => { }}
              />
            </div>
          </div>
        </PageLayout.Toolbar>

        <Card className="overflow-hidden border-none shadow-sm">
          <Table
            columns={[
              { key: 'code', title: 'Mã hồ sơ' },
              { key: 'title', title: 'Nội dung đợt thanh toán' },
              { key: 'type', title: 'Loại', align: 'center' },
              { key: 'amount', title: 'Giá trị Net (VNĐ)', align: 'right' },
              { key: 'submitter', title: 'Người lập' },
              { key: 'status', title: 'Trạng thái', align: 'center' },
            ]}
            data={claims}
            isLoading={isLoading}
            onRowClick={(item) => navigate(`./detail/${item.id}`)}
            renderCell={(item, key) => {
              switch (key) {
                case 'code':
                  return <span className="font-mono text-[11px] font-bold text-os-primary  tracking-wider">{item.claim_code}</span>;
                case 'title':
                  return (
                    <div className="flex flex-col">
                      <span className="font-bold text-slate-900 text-xs">{item.claim_title}</span>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <span className="text-[10px] text-slate-400 font-bold  tracking-tighter italic">{item.project?.name}</span>
                      </div>
                    </div>
                  );
                case 'type':
                  return <Badge variant="neutral" className="text-[9px] font-bold ">{typeLabels[item.claim_type]}</Badge>;
                case 'amount':
                  return (
                    <div className="flex flex-col items-end">
                      <span className="text-sm font-bold text-slate-900">{item.net_amount.toLocaleString()}</span>
                      <span className="text-[9px] text-slate-400 italic">Gross: {item.gross_amount.toLocaleString()}</span>
                    </div>
                  );
                case 'submitter':
                  return (
                    <div className="flex items-center gap-2">
                      <Avatar name={item.submitter?.full_name} size="xs" src={item.submitter?.avatar_url} />
                      <span className="text-[10px] font-medium text-slate-600">{item.submitter?.full_name}</span>
                    </div>
                  );
                case 'status':
                  return (
                    <Badge className={`${statusColors[item.status_code]} border-transparent text-[9px] font-bold `}>
                      {item.status_code}
                    </Badge>
                  );
                default:
                  return null;
              }
            }}
          />
        </Card>
      </div>
    </PageLayout>
  );
}
